import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const UpcomingSessionCard = ({ session, onModify, onCancel }) => {
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date?.toLocaleDateString('en-US', { 
      weekday: 'short', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  const formatTime = (time) => {
    const [hours, minutes] = time?.split(':');
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour % 12 || 12;
    return `${displayHour}:${minutes} ${ampm}`;
  };

  const getEncounterTypeIcon = (type) => {
    const icons = {
      romantic: 'Heart',
      passionate: 'Flame',
      playful: 'Smile',
      relaxed: 'Cloud',
      spontaneous: 'Zap'
    };
    return icons?.[type] || 'Heart';
  };

  const getCompatibilityColor = (score) => {
    if (score >= 80) return 'text-success';
    if (score >= 50) return 'text-warning';
    return 'text-accent';
  };

  return (
    <div className="bg-card rounded-lg border border-border p-4 md:p-5 hover:shadow-therapeutic transition-therapeutic">
      <div className="flex items-start justify-between mb-3 md:mb-4">
        <div className="flex items-center space-x-3">
          <div className="flex items-center justify-center w-10 h-10 md:w-12 md:h-12 bg-primary/10 rounded-md flex-shrink-0">
            <Icon 
              name={getEncounterTypeIcon(session?.encounterType)} 
              size={20} 
              color="var(--color-primary)" 
            />
          </div>
          <div>
            <h4 className="text-base md:text-lg font-heading font-semibold text-foreground">
              {session?.encounterTypeLabel}
            </h4>
            <p className="text-sm text-muted-foreground mt-0.5">
              {formatDate(session?.date)} at {formatTime(session?.time)}
            </p>
          </div>
        </div>
        
        {session?.status === 'confirmed' && (
          <div className="flex items-center space-x-1 px-2 py-1 bg-success/10 rounded-md flex-shrink-0">
            <Icon name="Check" size={14} color="var(--color-success)" />
            <span className="text-xs font-medium text-success">Confirmed</span>
          </div>
        )}
        
        {session?.status === 'pending' && (
          <div className="flex items-center space-x-1 px-2 py-1 bg-warning/10 rounded-md flex-shrink-0">
            <Icon name="Clock" size={14} color="var(--color-warning)" />
            <span className="text-xs font-medium text-warning">Pending</span>
          </div>
        )}
      </div>
      <div className="space-y-2 mb-4">
        <div className="flex items-center space-x-2 text-sm">
          <Icon name="Clock" size={16} color="var(--color-muted-foreground)" />
          <span className="text-muted-foreground">Duration: {session?.duration} minutes</span>
        </div>
        
        {session?.location && (
          <div className="flex items-center space-x-2 text-sm">
            <Icon name="MapPin" size={16} color="var(--color-muted-foreground)" />
            <span className="text-muted-foreground">{session?.locationLabel}</span>
          </div>
        )}

        <div className="flex items-center space-x-2 text-sm">
          <Icon name="Activity" size={16} color="var(--color-muted-foreground)" />
          <span className="text-muted-foreground">
            Compatibility: 
            <span className={`ml-1 font-medium ${getCompatibilityColor(session?.compatibilityScore)}`}>
              {session?.compatibilityScore}%
            </span>
          </span>
        </div>
      </div>
      {session?.aiSuggestion && (
        <div className="bg-accent/5 rounded-md p-3 mb-4">
          <div className="flex items-start space-x-2">
            <Icon name="Sparkles" size={16} color="var(--color-accent)" className="mt-0.5 flex-shrink-0" />
            <p className="text-xs md:text-sm text-foreground">
              <span className="font-medium">AI Insight:</span> {session?.aiSuggestion}
            </p>
          </div>
        </div>
      )}
      <div className="flex flex-col sm:flex-row gap-2">
        <Button
          variant="outline"
          size="sm"
          iconName="Edit"
          iconPosition="left"
          onClick={() => onModify(session)}
          fullWidth
        >
          Modify
        </Button>
        <Button
          variant="ghost"
          size="sm"
          iconName="X"
          iconPosition="left"
          onClick={() => onCancel(session?.id)}
          fullWidth
        >
          Cancel Session
        </Button>
      </div>
    </div>
  );
};

export default UpcomingSessionCard;